import { authenticate, makeSalesChannel } from '@commercelayer/js-auth';
import type {
  Address,
  AddressCreate,
  AddressUpdate,
  BraintreePayment,
  Bundle,
  Customer,
  CustomerAddress,
  LineItem,
  LineItemOption,
  Order,
  OrderCreate,
  OrderUpdate,
  QueryPageSize,
  Shipment,
  Sku,
  SkuOption,
} from '@commercelayer/sdk';
import CommerceLayer, {
  addresses,
  braintree_payments,
  bundles,
  CommerceLayerStatic,
  customer_addresses,
  customer_subscriptions,
  customers,
  line_item_options,
  line_items,
  orders,
  payment_methods,
  shipments,
  shipping_methods,
  sku_options,
  skus,
} from '@commercelayer/sdk';
import fakeAddresses from '@data/addresses.json';
import { SiteLocale } from '@graphql/generated';
import { ProductAndBundleDataType } from '@model/product';
import { AddressFormData, BillingAddressFormData } from '@src/types/address';
import { api } from '@utils/config';
import {
  deleteCartIdSession,
  deleteUserSession,
  getCartIdSession,
  getUserSession,
  setCartIdSession,
  setUserSession,
  UserData,
} from '@utils/local-storage';
import { AxiosError } from 'axios';
import { createStorage } from 'unstorage';
import localStorageDriver from 'unstorage/drivers/localstorage';
import { trackAddToCart, trackRemoveFromCart, trackSignUp } from './utils/gtmUtils';

const SRV_URL = '/api';
const ENDPOINT = process.env.CL_ENDPOINT;
const CLIENT_ID = process.env.CL_CLIENT_ID;

export type AddressType = 'billing' | 'shipping';

export class AppCommerceLayerClient {
  private order: Order;
  private customer: Customer;
  private apiClientUrl: string = '';

  constructor(
    private marketId: string,
    private countryCode: string
  ) {
    this.apiClientUrl = ENDPOINT + '/api';
  }

  public async init() {
    const token = await this.getToken();
    const cl = CommerceLayer({
      accessToken: token.accessToken,
    });
    cl.addRawResponseReader();
    if (!this.customer) {
      const userData = getUserSession();
      if (userData) {
        try {
          const customer = await customers.retrieve(userData.owner_id, {
            include: ['customer_addresses.address'],
          });
          this.customer = customer;
        } catch (error) {
          this.cleanup();
          this.logError('loginFromSession ERROR', error);
          await this.checkError(error);
        }
      }
    }
  }

  public getOrderId() {
    return getCartIdSession();
  }

  public cleanup() {
    this.deleteCart();
    deleteUserSession();
    this.customer = undefined;
  }

  public async getProductList(skuCode: string[]): Promise<Sku[]> {
    const skuList: Sku[] = [];
    try {
      let nextPage = true;
      let currentPage = 1;
      while (nextPage) {
        const response = await skus.list({
          filters: { code_matches_any: skuCode.join(',') },
          include: ['sku_options', 'prices', 'stock_items', 'stock_items.reserved_stock'],
          pageNumber: currentPage,
          pageSize: 25,
        });

        skuList.push(...response);

        currentPage++;

        nextPage = currentPage <= response.meta.pageCount;
      }
    } catch (error) {
      this.logError('getProductList', error);
    }
    return this.calculateSkusInventoryFromStockItems(skuList);
  }

  /**
   * @deprecated please use getProduct
   */
  public async getProductFromSku(skuCode: string): Promise<Sku | null> {
    if (!skuCode) {
      return null;
    }
    try {
      const skuList = await skus.list({
        filters: { code_matches_any: skuCode },
      });
      if (!skuList?.first()) {
        return null;
      }

      //skus.list doesn't return inventory object so we need to do a skus.retrieve call
      return this.getProduct(skuList.first()!.id);
    } catch (error) {
      this.logError('getProductFromSku', error);
    }
  }

  public async getProduct(id: string): Promise<Sku | null> {
    try {
      return await skus.retrieve(id, {
        include: ['sku_options', 'prices', 'stock_items', 'stock_items.reserved_stock'],
      });
    } catch (error) {
      this.logError('getProduct', error);
    }
  }

  public async getBundleList(skuCode: string[]): Promise<Bundle[]> {
    const bundleList: Bundle[] = [];
    try {
      let nextPage = true;
      let currentPage = 1;
      while (nextPage) {
        const response = await bundles.list({
          filters: { code_matches_any: skuCode.join(',') },
          include: [
            'skus',
            'skus.stock_items',
            'sku_list.sku_list_items',
            'skus.stock_items.reserved_stock',
          ],
          pageNumber: currentPage,
          pageSize: 25,
        });
        bundleList.push(...response);
        currentPage++;

        nextPage = currentPage <= response.meta.pageCount;
      }
    } catch (error) {
      this.logError('getBundleList', error);
    }
    return bundleList.map((bundle: Bundle) => {
      bundle.skus = this.calculateSkusInventoryFromStockItems(bundle.skus);
      return bundle;
    });
  }

  /**
   * @deprecated please use getBundle
   */
  public async getBundleFromSku(skuCode: string): Promise<Bundle | null> {
    if (!skuCode) {
      return null;
    }
    try {
      const bundleList = await bundles.list({
        filters: { code_matches_any: skuCode },
      });
      if (!bundleList?.first()) {
        return null;
      }

      //bundles.list doesn't return inventory object so we need to do a bundles.retrieve call
      return await this.getBundle(bundleList.first()!.id);
    } catch (error) {
      this.logError('getBundleFromSku', error);
    }
  }

  public async getBundle(id: string) {
    try {
      return await bundles.retrieve(id, {
        include: [
          'skus',
          'skus.stock_items',
          'sku_list.sku_list_items',
          'skus.stock_items.reserved_stock',
        ],
      });
    } catch (error) {
      this.logError('getBundle', error);
    }
  }

  public async login(user: { email?: string; password?: string }) {
    if (!user.email || !user.password) {
      throw 'WRONG_CREDENTIALS';
    }

    try {
      const customerCredentials = await this.getCustomerCredentials(user.email, user.password);
      if (customerCredentials.errors) {
        throw 'WRONG_CREDENTIALS';
      }

      const salesChannel = this.getSalesChannel();

      //await salesChannel.removeAuthorization();

      /**
       * Set the customer credentials in the storage.
       */
      await salesChannel.setCustomer({
        accessToken: customerCredentials.accessToken,
        scope: customerCredentials.scope,
        /**
         * when `refreshToken` is provided, it'll be used
         * to automatically refresh the customer access token when it expires
         */
        refreshToken: customerCredentials.refreshToken,
      });

      /**
       * Get the current customer authorization.
       */
      const customerAuthorization = await salesChannel.getAuthorization();

      const userData: UserData = {
        email: user.email,
        owner_id: customerCredentials.ownerId,
        accessToken: customerCredentials.accessToken,
        refreshToken: customerCredentials.refreshToken,
      };
      setUserSession(userData);
      await this.init();
      await this.postLoginOperations();
      return userData;
    } catch (error) {
      if (error != 'WRONG_CREDENTIALS') {
        this.logError('LOGIN ERROR', error);
      }
      throw error;
    }
  }

  public async register(
    user: { email: string; password: string },
    metadata: string,
    subscribeConsent = false
  ) {
    const { email, password } = user;
    return await this.doRegister({ email, password, metadata, subscribeConsent });
  }

  public async logout() {
    this.cleanup();
    const salesChannel = this.getSalesChannel();
    /**
     * Logout the current customer.
     * This will remove the customer authorization from the storage,
     * and revoke the access token.
     */
    await salesChannel.logoutCustomer();
    await this.init();
    await this.createOrder();
  }

  public countOrderProductsQuantity(order: Order): number {
    const lineItems = order?.line_items;
    if (!lineItems) {
      return 0;
    }
    return lineItems
      .filter((i) => i.item_type === 'skus' || i.item_type === 'bundles')
      .map((lineItem) => lineItem.quantity)
      .reduce((acc, value) => acc + value, 0);
  }

  public async addToCart(
    product: ProductAndBundleDataType,
    indexGtm: number,
    incQuantity = 1,
    itemListName: string
  ) {
    let result = {
      error: undefined as unknown,
      success: true,
      image: product.image,
      name: product.name,
      incQuantity,
    };

    const { id, skuCode, name, image, option, maxQuantity, levels, url, metadata } = product;
    if (!this.order) {
      this.order = await this.getOrder();
    }
    if (!this.order) {
      this.order = await this.createOrder();
    }
    try {
      const quantity = incQuantity;

      const lineItem = await line_items.create(
        {
          order: orders.relationship(this.order.id),
          item: skus.relationship(id),
          quantity,
          sku_code: skuCode,
          name,
          image_url: image.src,
          _update_quantity: true,
          metadata: {
            ...metadata,
            maxQuantity,
            levels,
            url,
          },
        },
        {
          include: ['line_item_options'],
        }
      );
      if (option) {
        await this.updateOrCreateLineItemOption(option, lineItem);
        // force refresh order for webhook shipping and taxes calculator
        await this.refreshOrder(this.order.id);
      }

      trackAddToCart(lineItem, incQuantity, indexGtm, itemListName);
    } catch (error) {
      this.logError('CART CREATE error', error);
      result = { ...result, error, success: false };
    }
    return result;
  }

  public async addBundleToCart(
    product: ProductAndBundleDataType,
    indexGtm: number,
    incQuantity = 1,
    itemListName: string
  ) {
    let result = {
      error: undefined as unknown,
      success: true,
      image: product.image,
      name: product.name,
      incQuantity,
    };
    const { skuCode, name, image, maxQuantity, levels, id, url, metadata, option } = product;
    let order = this.order;
    if (!order) {
      order = await this.getOrder();
    }
    if (!order) {
      order = await this.createOrder();
    }

    const quantity = incQuantity;
    try {
      const lineItem = await line_items.create(
        {
          order: orders.relationship(order.id),
          item: bundles.relationship(id),
          quantity: quantity,
          name,
          bundle_code: skuCode,
          image_url: image.src,
          _update_quantity: true,
          metadata: {
            ...metadata,
            maxQuantity,
            levels,
            url,
          },
        },
        {
          include: ['line_item_options'],
        }
      );
      if (option) {
        await this.updateOrCreateLineItemOption(option, lineItem);
        // force refresh order for webhook shipping and taxes calculator
        await this.refreshOrder(order.id);
      }

      lineItem.sku_code = lineItem.bundle_code;

      trackAddToCart(lineItem, quantity, indexGtm, itemListName);
    } catch (error) {
      this.logError('addBundleToCart create', error);
      result = { ...result, error, success: false };
    }
    return result;
  }

  public async removeLineItem(lineItem: LineItem) {
    try {
      await line_items.delete(lineItem.id);
      trackRemoveFromCart(lineItem);
    } catch (error) {
      this.logError('removeLineItem error', error);
    }
  }

  public async checkEtagUpdate(
    currentOrderEtag?: string,
    alreadyRefreshed: boolean = false
  ): Promise<Order | undefined> {
    for (let i = 0; i < 5; i++) {
      const order = await this.getOrder();
      const etag = order?.metadata?.etag;
      if (etag && etag !== currentOrderEtag) {
        return order;
      }
      await this.sleep(1000);
    }
    if (!alreadyRefreshed) {
      // trigger a refresh of the order and check again one last time
      await this.refreshOrder(this.getOrderId());
      return this.checkEtagUpdate(currentOrderEtag, true);
    }
  }

  public async refreshOrder(orderId: string): Promise<Order> {
    return await orders._refresh(orderId);
  }

  public async fetchOrder(id: string) {
    try {
      const order = await orders.retrieve(id, {
        include: [
          'line_items',
          'billing_address',
          'shipping_address',
          'available_payment_methods',
          'shipments.available_shipping_methods',
          'payment_method',
          'payment_source',
          'customer.customer_addresses.address',
        ],
      });
      this.order = order;
      return order;
    } catch (error) {
      this.logError('fetchOrder', error);
      await this.checkError(error);
    }
  }

  public async addCustomerEmail(orderId: string, email: string): Promise<Order> {
    try {
      return await orders.update({
        id: orderId,
        customer_email: email,
        //Order refresh for any user-related coupon validity/invalidity
        _refresh: true,
      });
    } catch (error) {
      if (!this.isCouponError(error)) {
        this.logError('addCustomerEmail', error);
        throw error;
      }
      // maybe the coupon is not valid for this user, remove the coupon and retry
      // TODO add a visible alert to the user informing him/her of the removal of the coupon
      try {
        return await orders.update({
          id: orderId,
          customer_email: email,
          coupon_code: null,
          //Order refresh for any user-related coupon validity/invalidity
          _refresh: true,
        });
      } catch (retryError) {
        this.logError('setOrderCustomer retry error', retryError);
        throw retryError;
      }
    }
  }

  public async setPaymentMethod(orderId: string, paymentMethodId: string): Promise<Order> {
    try {
      const order = await orders.update({
        id: orderId,
        payment_method: payment_methods.relationship(paymentMethodId),
      });
      return order;
    } catch (error) {
      this.logError('setPaymentMethod error', error);
      throw error;
    }
  }

  public async deletePaymentSource(type: string, id: string) {
    const url = `${this.apiClientUrl}/${type}/${id}`;
    const res = await api.delete(url, { headers: await this.getHeaders() }).catch(function (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error(error.response.data, error.response.status, error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.error(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error', error.message);
      }
      console.error(error.config);
    });
    if (res) {
      return res.data;
    }
  }

  public async setPaymentSource(orderId: string, type = 'braintree_payments') {
    const url = `${this.apiClientUrl}/${type}`;
    try {
      const res = await api.post(
        url,
        {
          data: {
            type,
            attributes: {},
            relationships: {
              order: {
                data: {
                  type: 'orders',
                  id: orderId,
                },
              },
            },
          },
        },
        { headers: await this.getHeaders() }
      );

      return res.data;
    } catch (error) {
      this.logError('setPaymentSource error', error);
      throw error;
    }
  }

  public async updateBraintreePaymentSource(
    sourceId: string,
    payment_method_nonce: string | null
  ): Promise<BraintreePayment> {
    try {
      const res = await braintree_payments.update({
        id: sourceId,
        payment_method_nonce,
      });
      return res;
    } catch (error) {
      this.logError('updateBraintreePaymentSource error', error);
      throw error;
    }
  }

  public async placeOrder(
    orderId: string,
    shipId: string,
    billId: string,
    metadata = {},
    locale: SiteLocale = SiteLocale.En
  ): Promise<Order> {
    try {
      const res = await orders.update({
        id: orderId,
        _place: true,
        language_code: locale,
        metadata: metadata,
        _shipping_address_clone_id: shipId,
        _billing_address_clone_id: billId,
      });
      return res;
    } catch (error) {
      this.logError('placeOrder', error);
      throw error;
    }
  }

  public async addAddressToCustomer(
    customerId: string,
    addressId: string
  ): Promise<CustomerAddress> {
    try {
      // @ts-expect-error no customer_email needed
      const res = customer_addresses.create({
        customer: customers.relationship(customerId),
        address: addresses.relationship(addressId),
      });
      return res;
    } catch (error) {
      this.logError('addAddressToCustomer', error);
    }
  }

  public async setAddresses(attributes: {
    orderId: string;
    billId?: string;
    shipId?: string;
  }): Promise<Order> {
    try {
      const updateObject: OrderUpdate = {
        id: attributes.orderId,
        //_billing_address_clone_id: attributes.billId,
        //_shipping_address_clone_id: attributes.shipId,
      };

      if (attributes.billId) {
        updateObject.billing_address = addresses.relationship(attributes.billId);
      }
      if (attributes.shipId) {
        updateObject.shipping_address = addresses.relationship(attributes.shipId);
      }
      const res = await orders.update(updateObject, {
        include: ['billing_address', 'shipping_address'],
      });

      return res;
    } catch (error) {
      this.logError('setAddresses', error);
    }
  }

  public async setShipping(attributes: {
    shipmentId: string;
    shippingMethodId: string;
  }): Promise<Shipment> {
    try {
      const res = await shipments.update({
        id: attributes.shipmentId,
        shipping_method: shipping_methods.relationship(attributes.shippingMethodId),
      });
      return res;
    } catch (error) {
      this.logError('setShipping', error);
      throw error;
    }
  }

  public async removeCoupon(orderId: string): Promise<Order> {
    try {
      return await orders.update({
        id: orderId,
        coupon_code: null,
      });
    } catch (error) {
      this.logError('removeCoupon', error);
      throw error;
    }
  }

  public async addCoupon(attributes: { orderId: string; code: string }): Promise<Order> {
    return await orders.update({
      id: attributes.orderId,
      coupon_code: attributes.code,
    });
  }

  public async subscribeToProfilingOrNewsletter(attributes: {
    email: string;
    metadata: { consent_type: string };
  }) {
    const { email, metadata } = attributes;
    try {
      return await customer_subscriptions.create({
        customer_email: email,
        reference: metadata?.consent_type || 'Newsletter',
        metadata: metadata,
      });
    } catch (error: any) {
      if (CommerceLayerStatic.isApiError(error)) {
        if (error.errors[0].status == 422) {
          // ignore error 422 because it is caused by a subscription already made
          //"reference - has already been taken"
          return;
        }
      }
      this.logError('subscribeToProfilingOrNewsletter', error);
    }
  }

  public async resetPassword(attributes: { email: string; url: string; language: string }) {
    const url = `${SRV_URL}/resetPassword`;
    const res = await api.post(url, attributes, { headers: this.headers });
    return res.data;
  }

  public async changePassword(attributes: { password: string; id: string; token: string }) {
    try {
      const url = `${SRV_URL}/changePassword`;
      const res = await api.post(url, attributes, { headers: this.headers });
      return res.data;
    } catch (error) {
      this.logError('changePassword', error);
      throw error;
    }
  }

  public async updateCustomer(attributes: Record<string, any>) {
    const userSession = this.getCustomer();
    const customer = customers.update({
      id: userSession?.id,
      ...attributes,
    });
    return customer;
  }

  public async updateAddress(
    attributes: BillingAddressFormData | Address | AddressFormData
  ): Promise<Address> {
    try {
      const address = this.getAddressUpdate(attributes);
      return await addresses.update(address);
    } catch (error) {
      this.logError('updateAddress', error);
      throw error;
    }
  }

  public async createAddress(attributes: Address | BillingAddressFormData | AddressFormData) {
    try {
      const address = this.getAddressCreate(attributes);
      return await addresses.create(address);
    } catch (error) {
      this.logError('createAddress', error);
      throw error;
    }
  }

  public async updateItemAvailability(
    itemId: string,
    availability: number | undefined
  ): Promise<LineItem> {
    try {
      const res = await line_items.update({
        id: itemId,
        quantity: availability,
        metadata: { availability },
      });
      return res;
    } catch (error) {
      this.logError('updateItemAvailability', error);
      throw error;
    }
  }

  public async getOrder(): Promise<Order> {
    const cartId = this.getOrderId();
    if (!cartId) {
      return undefined;
    }
    try {
      let order = undefined;
      try {
        order = await this.fetchOrder(cartId);
      } catch (err) {
        this.deleteCart();
      }

      if (!order || (order.status !== 'pending' && order.status !== 'draft')) {
        order = await this.createOrder();
      }

      this.order = order;
      return order;
    } catch (error) {
      this.logError('GET ORDER ERROR', error);
      await this.checkError(error);
    }
  }

  public async updateLineItemQuantity(lineItem: LineItem, newQuantity: number) {
    const { quantity } = lineItem;
    const updatedLineItem = await this.updateLineItemQty(lineItem.id, newQuantity);
    const lineItemOptions = updatedLineItem.line_item_options;
    if (lineItemOptions && lineItemOptions.length > 0) {
      lineItemOptions.forEach(async (lineItemOption) => {
        await this.updateLineItemOptionQty(lineItemOption.id, newQuantity);
      });
      // force refresh order for webhook shipping and taxes calculator
      await this.refreshOrder(this.order.id);
    }
    //Handle for increment or decrement items from cart page
    if (quantity && quantity >= newQuantity) {
      trackRemoveFromCart(lineItem, quantity - newQuantity);
    } else if (quantity) {
      trackAddToCart(lineItem, newQuantity - quantity);
    }
  }

  public getCustomer = () => {
    return this.customer;
  };

  public async deleteAddressAndCustomerAddressesById(
    customerAddressesId: string,
    addressId: string
  ) {
    await customer_addresses.delete(customerAddressesId);
    await addresses.delete(addressId);

    await this.verifyOrderConsistency(addressId);
  }

  public async getAddressList(countryCode: string): Promise<CustomerAddress[]> {
    const customerAddresses: CustomerAddress[] = [];
    if ((await this.getToken()).ownerType === 'customer') {
      const PAGE_SIZE: QueryPageSize = 25;

      let goOn = true;
      let pageNumber = 0;
      while (goOn) {
        pageNumber += 1;
        const params = {
          include: ['address'],
          pageNumber,
          pageSize: PAGE_SIZE,
          filters: {},
        };
        if (countryCode) {
          params.filters = { address_country_code_eq: countryCode };
        }

        const response = await customer_addresses.list(params);

        if (!response || response?.length < PAGE_SIZE) {
          goOn = false;
        }
        customerAddresses.push(...response!);
      }
      return customerAddresses.filter((customerAddress) => {
        return this.isValidCustomerAddress(customerAddress);
      });
    }
    return customerAddresses;
  }

  public isValidCustomerAddress(customerAddress: CustomerAddress) {
    return customerAddress.address?.metadata?.type !== 'test';
  }

  public async getAddressById(id: string) {
    return await addresses.retrieve(id);
  }

  public upsertAddress = async (
    addressFormData: BillingAddressFormData | AddressFormData | Address
  ) => {
    if (addressFormData.id) {
      return await this.updateAddress(addressFormData);
    } else {
      const address = await this.createAddress(addressFormData);
      const customer = await this.getCustomer();
      return await customer_addresses.create({
        customer_email: customer.email,
        customer: customers.relationship(customer.id),
        address: addresses.relationship(address.id),
      });
    }
  };

  public async getToken() {
    try {
      const salesChannel = this.getSalesChannel();
      const auth = await salesChannel.getAuthorization();
      return auth;
    } catch (error) {
      this.logError('getToken error', error);
      const salesChannel = this.getSalesChannel();
      // if there is an error getting the token, remove the authorization and try again
      salesChannel.removeAuthorization();
    }
    const salesChannel = this.getSalesChannel();
    const auth = await salesChannel.getAuthorization();
    return auth;
  }

  public deleteCart() {
    deleteCartIdSession();
    this.order = undefined;
  }

  public async getCustomerCredentials(username: string, password: string) {
    const scope = `market:id:${this.marketId}`;
    /**
     * Authenticate a customer using their email and password, or
     * though the JWT bearer flow.
     */
    const customerCredentials = await authenticate('password', {
      clientId: CLIENT_ID,
      scope: scope,
      username: username,
      password: password,
    });

    return customerCredentials;
  }

  private calculateSkusInventoryFromStockItems = (skus: Sku[]) => {
    return skus.map((sku) => {
      const quantity = this.sumStockItemsQuantity(sku);
      sku.inventory = { ...sku.inventory, quantity, available: quantity > 0 };
      return sku;
    });
  };

  private sumStockItemsQuantity = (sku: Sku) =>
    (sku.stock_items || []).reduce((qty, stockItem) => {
      return (qty += (stockItem?.quantity || 0) - (stockItem.reserved_stock?.quantity || 0));
    }, 0);

  private async postLoginOperations() {
    const customer = this.getCustomer();
    if (customer.customer_addresses) {
      const addresses = customer.customer_addresses.filter(
        (a) => a.address?.country_code === this.countryCode
      );
      if (addresses.length > 0) {
        await this.migrateAddressesIfNeeded(addresses);
      }
      this.removeInvalidAddress(customer.customer_addresses);
    }

    const order = await this.getOrder();
    if (order) {
      await this.setOrderCustomer(order.id, customer.id);
    }
  }

  private async removeInvalidAddress(customerAddresses: CustomerAddress[]) {
    customerAddresses.forEach(async (customerAddress) => {
      // state_code for US country must be 2 char lenght
      if (
        customerAddress.address.country_code == 'US' &&
        customerAddress.address.state_code.length != 2
      ) {
        await customer_addresses.delete(customerAddress.id);
        await addresses.delete(customerAddress.address.id);
      }
      if (customerAddress.address?.metadata?.type == 'test') {
        //Removing fake customer addresses
        await customer_addresses.delete(customerAddress.id);
      }
    });
  }

  private async migrateAddressesIfNeeded(customerAddresses: CustomerAddress[]) {
    const oldVersionAddresses = this.extractOldAddresses(customerAddresses);
    if (oldVersionAddresses.length > 0) {
      for await (const customerAddress of oldVersionAddresses) {
        if (customerAddress.address) {
          await this.updateAddress(customerAddress.address);
        }
      }
    }
  }

  private extractOldAddresses(customerAddresses: CustomerAddress[]) {
    return customerAddresses.filter(
      (customerAddress) =>
        !(
          customerAddress.address?.metadata &&
          'isBilling' in customerAddress.address?.metadata &&
          'isShipping' in customerAddress.address?.metadata
        )
    );
  }

  private async doRegister(attributes: {
    email: string;
    password: string;
    metadata: string;
    subscribeConsent: boolean;
  }) {
    const url = `${SRV_URL}/signup`;
    try {
      const res = await api.post(url, attributes, { headers: this.headers });
      trackSignUp();
      return res.data;
    } catch (error) {
      throw error;
    }
  }

  private async updateOrCreateLineItemOption(option: SkuOption, lineItem: LineItem) {
    const quantity = lineItem.quantity;
    const lineItemOptions = lineItem?.line_item_options;
    if (lineItemOptions && lineItemOptions.length > 0) {
      // line item options already present
      // update line item options quantity
      lineItemOptions.forEach(async (lineItemOption) => {
        await this.updateLineItemOptionQty(lineItemOption.id, quantity);
      });
    } else {
      //create line item options
      await line_item_options.create({
        name: option.name,
        quantity: quantity,
        options: { description: option.description },
        line_item: line_items.relationship(lineItem.id),
        sku_option: sku_options.relationship(option.id),
      });
    }
  }

  private async checkError(error: any) {
    if (CommerceLayerStatic.isApiError(error)) {
      if (error.status == 401) {
        // https://docs.commercelayer.io/core/how-tos/faq/authentication-and-access-tokens#a-cached-token-that-has-not-expired-yet-seems-to-be-invalid.-what-happened
        // cached token that has not expired is invalid
        this.cleanup();
        await this.init();
      }
    }
  }

  private async setOrderCustomer(orderId: string, customerId: string): Promise<Order> {
    const customer = customers.relationship(customerId);
    try {
      return await orders.update({
        id: orderId,
        customer: customer,
        //Order refresh for any user-related coupon validity/invalidity
        _refresh: true,
      });
    } catch (error) {
      if (!this.isCouponError(error)) {
        this.logError('setOrderCustomer error', error);
        throw error;
      }
      // maybe the coupon is not valid for this user, remove the coupon and retry
      // TODO add a visible alert to the user informing him/her of the removal of the coupon
      try {
        return await orders.update({
          id: orderId,
          customer: customer,
          coupon_code: null,
          //Order refresh for any user-related coupon validity/invalidity
          _refresh: true,
        });
      } catch (retryError) {
        this.logError('setOrderCustomer retry error', retryError);
        throw retryError;
      }
    }
  }

  private async getHeaders() {
    const headers = {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      Authorization: `Bearer ${(await this.getToken())?.accessToken}`,
    };
    return headers;
  }

  private get headers() {
    return { accept: 'Accept: application/json', 'content-type': 'application/json' };
  }

  private getAddressCreate(
    attributes: Address | BillingAddressFormData | AddressFormData
  ): AddressCreate {
    const address: AddressCreate = {
      business: !!attributes.company,
      first_name: attributes.first_name,
      last_name: attributes.last_name,
      company: attributes.company,
      line_1: attributes.line_1,
      line_2: attributes.line_2,
      city: attributes.city,
      zip_code: attributes.zip_code,
      state_code: attributes.state_code,
      country_code: attributes.country_code,
      phone: attributes.phone,
      metadata: {
        ...attributes.metadata,
        lastUsage: new Date().getTime(),
      },
    };

    //BillingAddressFormData
    if ('person_type' in attributes) {
      address.metadata = {
        ...attributes.metadata,
        lastUsage: new Date().getTime(),
        person_type: attributes.person_type,
        sdi: attributes.sdi,
        vat: attributes.vat,
        iban: attributes.iban,
        fiscal_code: attributes.fiscal_code,
        pec: attributes.pec,
      };
    }

    if (address.metadata.person_type) {
      address.metadata.isShipping = false;
      address.metadata.isBilling = true;
    } else {
      address.metadata.isShipping = true;
      address.metadata.isBilling = false;
    }

    return address;
  }

  private getAddressUpdate(
    attributes: Address | BillingAddressFormData | AddressFormData
  ): AddressUpdate {
    const address: AddressUpdate = {
      id: attributes.id,
      ...this.getAddressCreate(attributes),
    };
    return address;
  }

  private async updateLineItemQty(id: string, quantity: number): Promise<LineItem> {
    try {
      const res = await line_items.update(
        {
          id: id,
          quantity: quantity,
        },
        {
          include: ['line_item_options'],
        }
      );
      return res;
    } catch (error) {
      this.logError('updateLineItemQty', error);
      throw error;
    }
  }

  private async updateLineItemOptionQty(id: string, quantity: number): Promise<LineItemOption> {
    try {
      const res = await line_item_options.update({
        id: id,
        quantity: quantity,
      });
      return res;
    } catch (error) {
      this.logError('updateLineItemOptionQty', error);
      throw error;
    }
  }

  private logError(context: string, error: any) {
    if (error instanceof AxiosError) {
      console.error(context, 'AxiosError', error.response ?? error.message);
    } else if (CommerceLayerStatic.isApiError(error)) {
      console.error(context, 'ApiError', error.errors);
    } else if (CommerceLayerStatic.isSdkError(error)) {
      console.error(context, 'SdkError', { message: error.message, name: error.name });
    } else {
      console.error(context, error);
    }
  }

  private isCouponError(error: any): boolean {
    return error?.errors[0]?.detail && error.errors[0].detail.includes('coupon_code');
  }

  private async createOrder(): Promise<Order> {
    try {
      const createObject: OrderCreate = {
        shipping_country_code_lock: this.countryCode,
      };
      const fakeShippingAddress = await this.getFakeShippingAddress();
      if (fakeShippingAddress) {
        createObject.shipping_address = fakeShippingAddress;
      }
      const order = await orders.create(createObject);

      setCartIdSession(order.id);

      this.order = order;
      return order;
    } catch (error) {
      this.logError('createOrder', error);
    }
    throw 'ERROR, CANNOT CREATE AN ORDER';
  }

  private async getFakeShippingAddress() {
    try {
      const address = (fakeAddresses as Address[]).find(
        (sc) => sc.country_code.toUpperCase() == this.countryCode.toUpperCase()
      );
      if (address) {
        return await addresses.retrieve(address.id);
      }
    } catch (error) {
      this.logError('getFakeShippingAddress', error);
    }
    return null;
  }

  private verifyOrderConsistency = async (addressId: string) => {
    const orderId = this.getOrderId();
    if (orderId) {
      const order = await this.fetchOrder(orderId);

      const billId =
        order?.billing_address?.id === addressId ? undefined : order?.billing_address?.id;
      const shipId =
        order?.shipping_address?.id === addressId ? undefined : order?.shipping_address?.id;

      if (!billId || !shipId) {
        this.setAddresses({ orderId, billId, shipId });
      }
    }
  };

  private getSalesChannel() {
    const salesChannel = makeSalesChannel(
      {
        clientId: CLIENT_ID,
        scope: `market:id:${this.marketId}`,
      },
      {
        /**
         * you can use any storage implementation you prefer or implement your own
         * in this example we use `unstorage` with the `localStorage` driver
         */
        storage: createStorage({
          driver: localStorageDriver({}),
        }),
      }
    );
    return salesChannel;
  }

  private async sleep(delay: number) {
    return new Promise((resolve) => setTimeout(resolve, delay));
  }
}
